import { sessionProtectionToken } from './sessionProtection';
import { workspaceDestination } from './workspaceRoutes';
import './operational-crud.css';
import { StrictMode, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { parseReference, referenceForPath, type ReferenceDocument } from './reference';
import { importSourceLineCount, importTableRows, type ImportPreviewResult } from './importPreview';
import { ExecutiveCockpit } from './ExecutiveCockpit';
import { SignalQueue } from './SignalQueue';
import { KpiCatalog } from './KpiCatalog';
import { KpiDefinition } from './KpiDefinition';
import { PerformanceTrees } from './PerformanceTrees';
import { ExecutiveDashboard } from './ExecutiveDashboard';
import { DashboardWorkspace } from './DashboardWorkspace';
import { DirectionPortfolio } from './DirectionPortfolio';
import { KpiValueEntry } from './KpiValueEntry';
import { KpiImport } from './KpiImport';
import { KpiCertification } from './KpiCertification';
import { AdministrationCenter } from './AdministrationCenter';
import { formatMetricLabel } from './formatMetric';
import { getRoutePresentation } from './routePresentation';
import './styles.css';
import { PilotageShell } from './PilotageShell';
import { CollaborationProvider } from './Collaboration';
import { CollaborationWorkspace } from './CollaborationWorkspace';
import { PrerequisitesWorkspace } from './Prerequisites';
import { pilotageApp, rememberPilotageLocation } from './pilotageApps';

const OperationalWorkspace = lazy(() => import('./OperationalWorkspace').then(module => ({ default: module.OperationalWorkspace })));
const ObservatoireQos = lazy(() => import('./ObservatoireQos').then(module => ({ default: module.ObservatoireQos })));
const CouvertureQos = lazy(() => import('./couverture-qos/CouvertureQos').then(module => ({ default: module.CouvertureQos })));

const ROUTES = [
  '/mon-espace',
  '/mes-tableaux',
  '/collaboration', '/prerequis',
  '/cockpit',
  '/tableau-dg',
  '/pilotage-directions',
  '/catalogue-kpi',
  '/signaux',
  '/saisie-kpi',
  '/certification-kpi',
  '/import-kpi',
  '/kpis/nouveau',
  '/administration',
  '/connexion',
  '/observatoire-qos',
  '/couverture-qos',
  '/gouvernance-kpi/indicateur.html',
  '/gouvernance-kpi/certification.html', '/gouvernance-kpi/import-kpi.html', '/gouvernance-kpi/signals.html',
  '/direction/index.html', '/direction-pulse/index.html', '/pilotage-kpi/pilotage_kpi_dashboard_v8.html',
  '/arbres-kpi',
];
const LEGACY_REDIRECTS: Record<string, string> = {
  '/gouvernance-kpi/dashboard.html': '/cockpit',
  '/gouvernance-kpi/catalogue.html': '/catalogue-kpi',
  '/gouvernance-kpi/certification.html': '/certification-kpi',
  '/gouvernance-kpi/import-kpi.html': '/import-kpi',
  '/gouvernance-kpi/signals.html': '/signaux',
  '/gouvernance-kpi/indicateur.html': '/kpis/nouveau',
  '/direction/index.html': '/tableau-dg',
  '/direction-pulse/index.html': '/pilotage-directions',
  '/pilotage-kpi/pilotage_kpi_dashboard_v8.html': '/arbres-kpi',
};

function canonicalPath(url: URL): string {
  if (url.pathname === '/gouvernance-kpi/indicateur.html') {
    const code = url.searchParams.get('code') ?? url.hash.slice(1);
    if (/^[A-Za-z0-9_-]+$/.test(code)) return `/kpis/${code.toUpperCase()}`;
  }
  const path = LEGACY_REDIRECTS[url.pathname] ?? url.pathname;
  const destination = workspaceDestination(path, url.search);
  if (destination) { const target = new URL(destination, url.origin); url.search = target.search; return target.pathname; }
  return path;
}
function initialPath(): string {
  const url = new URL(location.href);
  const canonical = canonicalPath(url);
  if (canonical === location.pathname) return location.pathname;
  history.replaceState({}, '', `${canonical}${url.search}${url.hash}`);
  return canonical;
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (init?.method && !['GET', 'HEAD', 'OPTIONS'].includes(init.method.toUpperCase()) && !headers.has('RequestVerificationToken')) {
    headers.set('RequestVerificationToken', await sessionProtectionToken());
  }
  return fetch(path, { credentials: 'same-origin', ...init, headers });
}

function loadReferenceStyles(filenames: string[]) {
  const links = filenames.map((filename) => {
    const link = document.createElement('link');
    // Fetch destination CSS without applying it over the currently visible route.
    // activateStyles enables the complete stylesheet set after all links are ready.
    link.rel = 'stylesheet'; link.media = 'not all'; link.href = `/reference/${filename}`; link.dataset.referenceStyle = 'true';
    document.head.append(link);
    return link;
  });
  const ready = Promise.all(links.map((link) => new Promise<void>((resolve) => {
    // A CSSStyleSheet object can exist before its network response is complete.
    // Only the load/error event tells us when it's safe to reveal the new route.
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  })));
  return { links, ready };
}

function setText(root: ParentNode, selector: string, value: string | number) {
  const node = root.querySelector<HTMLElement>(selector);
  if (node) node.textContent = String(value);
}

function renderImportPreview(root: HTMLElement, preview: ImportPreviewResult) {
  const results = root.querySelector<HTMLElement>('[data-import-results]');
  const body = root.querySelector<HTMLTableSectionElement>('[data-preview-body]');
  if (!results || !body) return;
  body.replaceChildren();
  const appendCell = (row: HTMLTableRowElement, value: string | number) => {
    const cell = document.createElement('td'); cell.textContent = String(value); row.append(cell);
  };
  const makeStatus = (row: HTMLTableRowElement, valid: boolean, message: string) => {
    const cell = document.createElement('td'); const badge = document.createElement('span');
    badge.className = `row-status ${valid ? 'good' : 'error'}`; badge.textContent = message; cell.append(badge); row.append(cell);
  };
  for (const entry of importTableRows(preview)) {
    const row = document.createElement('tr');
    appendCell(row, entry.row); appendCell(row, entry.code); appendCell(row, entry.name);
    appendCell(row, entry.details); appendCell(row, entry.target);
    makeStatus(row, entry.valid, entry.status); body.append(row);
  }
  setText(results, '[data-total-lines]', importSourceLineCount(preview));
  setText(results, '[data-valid-lines]', preview.validRows); setText(results, '[data-warning-lines]', 0); setText(results, '[data-error-lines]', preview.errorRows);
  const badge = results.querySelector<HTMLElement>('[data-control-status]');
  if (badge) { badge.textContent = preview.errorRows ? 'Corrections requises' : 'Structure valide'; badge.className = `control-status ${preview.errorRows ? 'invalid' : 'valid'}`; }
  const confirm = results.querySelector<HTMLButtonElement>('[data-create-kpis]');
  if (confirm) { confirm.disabled = preview.errorRows > 0 || preview.validRows === 0; confirm.textContent = `✓ Créer ${preview.validRows} indicateur${preview.validRows > 1 ? 's' : ''}`; }
  results.hidden = false;
  root.setAttribute('data-import-batch', preview.batchId);
}

interface CertificationRow {
  id: string; directionCode: string; contractCode: string; kpiCode: string; kpiName: string; unit: string; target: number;
  periodStart: string; periodEnd: string; status: string; revision: number; proposedValue: number | null;
  lastComment: string | null;
}

function buildCertificationCard(item: CertificationRow, canReview: boolean, canAttachEvidence: boolean): HTMLElement {
  const card = document.createElement('article'); card.className = 'certification-card';
  card.dataset.certificationCard = ''; card.dataset.observationId = item.id;
  const header = document.createElement('div'); header.className = 'certification-header';
  const title = document.createElement('div');
  const code = document.createElement('p'); code.className = 'kpi-code'; code.textContent = item.kpiCode;
  const heading = document.createElement('h2'); heading.textContent = `Période ${item.periodStart}`;
  title.append(code, heading);
  const status = document.createElement('span'); status.className = `status ${item.status === 'rejected' || item.status === 'correction-requested' ? 'critical' : 'watch'}`;
  status.textContent = item.status === 'submitted' ? 'Soumise' : item.status === 'in-review' ? 'En revue' : item.status === 'certified' ? 'Certifiée · à publier' : item.status === 'published' ? 'Publiée' : item.status === 'rejected' ? 'Rejetée' : item.status === 'correction-requested' ? 'À corriger' : 'Brouillon';
  header.append(title, status); card.append(header);
  const metrics = document.createElement('dl'); metrics.className = 'certification-metrics';
  const metric = (label: string, value: string) => { const entry = document.createElement('div'); const term = document.createElement('dt'); term.textContent = label; const detail = document.createElement('dd'); detail.textContent = value; entry.append(term, detail); return entry; };
  const value = item.proposedValue === null ? 'Non calculable' : formatMetricLabel(item.proposedValue, item.unit, 4);
  const gap = item.proposedValue === null ? '—' : formatMetricLabel(item.proposedValue - item.target, item.unit, 4);
  metrics.append(metric('Valeur proposée', item.proposedValue === null ? value : formatMetricLabel(item.proposedValue, item.unit, 1, 1)), metric('Cible', formatMetricLabel(item.target, item.unit, 1, 1)), metric('Écart', item.proposedValue === null ? gap : formatMetricLabel(item.proposedValue - item.target, item.unit, 1, 1)), metric('Source', 'Saisie'));
  card.append(metrics);
  const provenance = document.createElement('details'); provenance.className = 'provenance';
  const summary = document.createElement('summary'); summary.textContent = 'Entrées et provenance';
  const provenanceContent = document.createElement('div'); provenanceContent.className = 'provenance-content'; provenanceContent.textContent = item.lastComment || 'Aucun commentaire de saisie.';
  const evidence = document.createElement('div'); evidence.className = 'evidence-attach';
  if (canAttachEvidence) {
    const drive = document.createElement('input'); drive.setAttribute('aria-label', 'Identifiant du drive SharePoint'); drive.placeholder = 'driveId SharePoint';
    const file = document.createElement('input'); file.setAttribute('aria-label', 'Identifiant du fichier SharePoint'); file.placeholder = 'itemId SharePoint';
    const attach = document.createElement('button'); attach.type = 'button'; attach.className = 'button'; attach.dataset.attachEvidence = ''; attach.textContent = 'Vérifier et associer la preuve';
    evidence.append(drive, file, attach);
  } else evidence.textContent = 'Associer une preuve requiert le rôle Contributeur ou Validateur.';
  provenance.append(summary, provenanceContent, evidence); card.append(provenance);
  const commentLabel = document.createElement('label'); commentLabel.className = 'sr-only'; commentLabel.htmlFor = `comment-${item.id}`; commentLabel.textContent = 'Commentaire requis pour rejet ou correction';
  const comment = document.createElement('textarea'); comment.id = `comment-${item.id}`; comment.placeholder = 'Commentaire obligatoire pour rejet ou correction';
  card.append(commentLabel, comment);
  const actions = document.createElement('div'); actions.className = 'certification-actions';
  if (canReview) {
    if (item.status === 'certified') {
      const publish = document.createElement('button'); publish.type = 'button'; publish.className = 'button button-primary'; publish.dataset.publish = ''; publish.textContent = 'Publier la valeur certifiée'; actions.append(publish);
    } else if (item.status === 'submitted') {
      const reject = document.createElement('button'); reject.type = 'button'; reject.className = 'button button-muted'; reject.dataset.reject = ''; reject.textContent = '× Rejeter';
      const correct = document.createElement('button'); correct.type = 'button'; correct.className = 'button button-muted'; correct.dataset.correction = ''; correct.textContent = 'Demander correction'; actions.append(reject, correct);
      const review = document.createElement('button'); review.type = 'button'; review.className = 'button button-primary'; review.dataset.review = ''; review.textContent = 'Prendre en revue'; actions.append(review);
    } else if (item.status === 'in-review') {
      const reject = document.createElement('button'); reject.type = 'button'; reject.className = 'button button-muted'; reject.dataset.reject = ''; reject.textContent = '× Rejeter';
      const correct = document.createElement('button'); correct.type = 'button'; correct.className = 'button button-muted'; correct.dataset.correction = ''; correct.textContent = 'Demander correction';
      const certify = document.createElement('button'); certify.type = 'button'; certify.className = 'button button-primary'; certify.dataset.certify = ''; certify.textContent = '✓ Certifier';
      actions.append(reject, correct, certify);
    }
  }
  card.append(actions);
  return card;
}

function updateCertificationActions(card: HTMLElement, status: string) {
  const badge = card.querySelector<HTMLElement>('.certification-header .status');
  if (badge) {
    badge.textContent = status === 'in-review' ? 'En revue' : 'Certifiée · à publier';
    badge.className = `status ${status === 'in-review' ? 'watch' : 'okay'}`;
  }
  const actions = card.querySelector<HTMLElement>('.certification-actions');
  if (!actions) return;
  actions.replaceChildren();
  const button = document.createElement('button'); button.type = 'button'; button.className = 'button button-primary';
  if (status === 'in-review') { button.dataset.certify = ''; button.textContent = '✓ Certifier'; actions.append(button); }
  else { button.dataset.publish = ''; button.textContent = 'Publier la valeur certifiée'; actions.append(button); }
  card.dataset.status = status;
}

function App() {
  const [path, setPath] = useState(initialPath);
  const [navigationVersion, setNavigationVersion] = useState(0);
  const [page, setPage] = useState<ReferenceDocument | null>(null);
  const [readyPath, setReadyPath] = useState('');
  const [transitionVisible, setTransitionVisible] = useState(true);
  const [status, setStatus] = useState('');
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [sessionApiAvailable, setSessionApiAvailable] = useState<boolean | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [userName, setUserName] = useState('');
  const isAdmin = roles.includes('Gouvernance.Administrator');
  const roleKey = roles.join('|');
  const shell = useRef<HTMLDivElement>(null);
  const pageRef = useRef<ReferenceDocument | null>(null);
  const activeReferenceStyles = useRef<HTMLLinkElement[]>([]);

  useEffect(() => {
    setTransitionVisible(true);
    if (readyPath !== path) return;
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 80 : 520;
    const timer = window.setTimeout(() => setTransitionVisible(false), duration);
    return () => window.clearTimeout(timer);
  }, [path, readyPath]);

  useEffect(() => {
    let alive = true;
    const markReady = () => { if (alive) setReadyPath(path); };
    const activateStyles = (links: HTMLLinkElement[] = []) => {
      links.forEach((link) => { link.media = 'all'; });
      activeReferenceStyles.current.filter((link) => !links.includes(link)).forEach((link) => link.remove());
      activeReferenceStyles.current = links;
    };
    const cleanPendingStyles = (links: HTMLLinkElement[] = []) => {
      alive = false;
      links.filter((link) => !activeReferenceStyles.current.includes(link)).forEach((link) => link.remove());
    };
    if (path === '/mes-tableaux') {
      activateStyles(); document.body.className = ''; document.body.dataset.page = 'dashboards';
      document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
      document.title = 'Mes tableaux — Gouvernance ARTCI'; pageRef.current = null;
      setPage({ bodyClass: '', bodyPage: 'dashboards', title: document.title, tree: null });
      markReady(); return () => cleanPendingStyles();
    }
    if (path === '/collaboration' || path === '/prerequis') {
      activateStyles(); document.body.className = 'collaboration-page'; document.body.dataset.page = 'collaboration';
      document.title = 'Collaboration — Gouvernance ARTCI';
      setPage({ bodyClass: 'collaboration-page', bodyPage: 'collaboration', title: document.title, tree: null });
      markReady(); return () => cleanPendingStyles();
    }
    if (['/mon-espace', '/saisie-kpi', '/certification-kpi', '/kpis/nouveau'].includes(path)) {
      activateStyles(); document.body.className = ''; document.body.dataset.page = 'workspace'; pageRef.current = null; document.title = 'Mon espace de travail — ARTCI';
      setPage({ bodyClass: '', bodyPage: 'workspace', title: document.title, tree: null }); markReady(); return () => cleanPendingStyles();
    }
    if (path === '/administration') {
      activateStyles();
      document.body.className = 'admin-page'; document.body.dataset.page = 'admin';
      document.title = 'Administration — Gouvernance ARTCI';
      setPage({ bodyClass: 'admin-page', bodyPage: 'admin', title: document.title, tree: null });
      markReady();
      return () => cleanPendingStyles();
    }
    if (path === '/couverture-qos') {
      activateStyles();
      document.body.className = ''; document.body.dataset.page = 'couverture-qos';
      document.title = 'Couverture QoS — ARTCI';
      pageRef.current = null;
      setPage({ bodyClass: '', bodyPage: 'couverture-qos', title: document.title, tree: null });
      markReady();
      return () => cleanPendingStyles();
    }
    if (path === '/observatoire-qos') {
      activateStyles();
      document.body.className = ''; document.body.dataset.page = 'observatoire-qos';
      document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
      document.title = 'Observatoire QoS — ARTCI';
      pageRef.current = null;
      setPage({ bodyClass: '', bodyPage: 'observatoire-qos', title: document.title, tree: null });
      markReady();
      return () => cleanPendingStyles();
    }
    if (path === '/saisie-kpi') {
      activateStyles();
      document.body.className = '';
      document.body.dataset.page = 'kpi-value-entry';
      document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
      document.title = 'Saisie des valeurs KPI — Gouvernance ARTCI';
      pageRef.current = null;
      setPage({ bodyClass: '', bodyPage: 'kpi-value-entry', title: document.title, tree: null });
      markReady();
      return () => cleanPendingStyles();
    }
    if (path === '/signaux') {
      const styles = loadReferenceStyles(['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = '';
        document.body.dataset.page = 'signals';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Signaux & décisions — Gouvernance — ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: '', bodyPage: 'signals', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path === '/import-kpi' || path === '/certification-kpi') {
      const isImport = path === '/import-kpi';
      const styles = loadReferenceStyles(['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = '';
        document.body.dataset.page = isImport ? 'import' : 'certification';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = isImport ? 'Importer des KPI — Gouvernance ARTCI' : 'File de certification KPI — ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: '', bodyPage: document.body.dataset.page, title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path === '/pilotage-directions') {
      const styles = loadReferenceStyles(['direction-pulse/styles.css', 'gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = '';
        document.body.dataset.page = 'direction-portfolio';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Pilotage des directions — Gouvernance ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: '', bodyPage: 'direction-portfolio', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path === '/direction/index.html' || path === '/tableau-dg') {
      const styles = loadReferenceStyles(['direction/styles.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = '';
        document.body.dataset.page = 'executive-board';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Board DG — Gouvernance ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: '', bodyPage: 'executive-board', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path === '/cockpit') {
      const styles = loadReferenceStyles(['gouvernance-kpi/styles.css', 'gouvernance-kpi/cockpit.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = '';
        document.body.dataset.page = 'dashboard';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Cockpit exécutif — Gouvernance KPI — ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: '', bodyPage: 'dashboard', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path === '/catalogue-kpi') {
      const styles = loadReferenceStyles(['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = 'catalogue-shell';
        document.body.dataset.page = 'catalogue';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Catalogue KPI — Gouvernance — ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: 'catalogue-shell', bodyPage: 'catalogue', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path.startsWith('/kpis/')) {
      const styles = loadReferenceStyles(['gouvernance-kpi/styles.css', 'gouvernance-kpi/suite.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = 'admin-page';
        document.body.dataset.page = 'indicator';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Définition KPI — Gouvernance — ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: 'admin-page', bodyPage: 'indicator', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    if (path === '/arbres-kpi') {
      const styles = loadReferenceStyles(['pilotage-kpi/styles.css']);
      void styles.ready.then(() => {
        if (!alive) return;
        activateStyles(styles.links);
        document.body.className = '';
        document.body.dataset.page = 'performance-trees';
        document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light';
        document.title = 'Arbres de performance — Gouvernance — ARTCI';
        pageRef.current = null;
        setPage({ bodyClass: '', bodyPage: 'performance-trees', title: document.title, tree: null });
        markReady();
      });
      return () => cleanPendingStyles(styles.links);
    }
    const route = referenceForPath(path);
    const styles = loadReferenceStyles(route.css);
    Promise.all([styles.ready, request(`/reference/${route.file}`, { cache: 'no-cache' }).then((response) => { if (!response.ok) throw new Error('Référence visuelle indisponible.'); return response.text(); })])
      .then(([, html]) => { if (!alive) return; activateStyles(styles.links); const parsed = { ...parseReference(html), scripts: route.scripts ?? [] }; document.body.className = parsed.bodyClass; document.body.dataset.page = parsed.bodyPage; document.documentElement.dataset.theme = localStorage.getItem('governance-theme') === 'dark' ? 'dark' : 'light'; document.title = `${parsed.title || 'Gouvernance'} — ARTCI`; pageRef.current = parsed; setPage(parsed); markReady(); })
      .catch(() => { if (alive) { setStatus('Cette page n’est pas disponible.'); markReady(); } });
    return () => cleanPendingStyles(styles.links);
  }, [path]);

  useEffect(() => {
    if (!page?.scripts?.length) return;
    const scripts = page.scripts.map((source) => {
      const element = document.createElement('script');
      element.src = `/reference/${source}`; element.async = false; element.dataset.pocRuntime = 'true';
      document.body.append(element); return element;
    });
    return () => scripts.forEach((script) => script.remove());
  }, [page]);

  useEffect(() => {
    if (!path.endsWith('/gouvernance-kpi/certification.html') || !page || pageRef.current === null) return;
    const loadQueue = async () => {
      const list = shell.current?.querySelector<HTMLElement>('.certification-list');
      if (!list) return;
      list.setAttribute('aria-busy', 'true');
      list.replaceChildren();
      const loading = document.createElement('p'); loading.className = 'empty-state'; loading.textContent = 'Chargement de la file de certification…'; list.append(loading);
      try {
        const responses = await Promise.all(['submitted', 'in-review', 'certified'].map((state) => request(`/api/governance/observations?status=${state}`)));
        const failed = responses.find((response) => !response.ok);
        if (failed) throw new Error(await apiError(failed));
        const batches = await Promise.all(responses.map((response) => response.json() as Promise<CertificationRow[]>));
        if (!location.pathname.endsWith('/gouvernance-kpi/certification.html') || !shell.current) return;
        const reviewOrder: Record<string, number> = { submitted: 0, 'in-review': 1, certified: 2 };
        const rows = batches.flat().sort((a, b) => b.periodStart.localeCompare(a.periodStart) || (reviewOrder[a.status] ?? 9) - (reviewOrder[b.status] ?? 9) || a.kpiName.localeCompare(b.kpiName));
        const listElement = shell.current.querySelector<HTMLElement>('.certification-list');
        const empty = Array.from(shell.current.querySelectorAll<HTMLElement>('.empty-state'))
          .find((element) => !element.closest('.certification-list'));
        if (!listElement) return;
        listElement.replaceChildren(...rows.map((row) => buildCertificationCard(row, isAdmin || roles.includes('Gouvernance.Validator'), isAdmin || roles.includes('Gouvernance.Validator') || roles.includes('Gouvernance.Contributor'))));
        if (empty) { empty.textContent = rows.length === 0 ? 'Aucune valeur soumise ne relève de votre périmètre.' : ''; empty.hidden = rows.length !== 0; }
      } catch (error) {
        if (!location.pathname.endsWith('/gouvernance-kpi/certification.html') || !shell.current) return;
        const listElement = shell.current.querySelector<HTMLElement>('.certification-list');
        const empty = Array.from(shell.current.querySelectorAll<HTMLElement>('.empty-state'))
          .find((element) => !element.closest('.certification-list'));
        listElement?.replaceChildren();
        if (empty) { empty.textContent = error instanceof Error ? error.message : 'Chargement de la file impossible.'; empty.hidden = false; empty.setAttribute('role', 'alert'); }
      } finally { shell.current?.querySelector('.certification-list')?.removeAttribute('aria-busy'); }
    };
    void loadQueue();
  }, [isAdmin, roleKey, path, page]);

  useEffect(() => {
    let alive = true;
    request('/auth/session').then(async (response) => {
      if (!alive) return;
      setSessionApiAvailable(response.ok || response.status === 401);
      setAuthenticated(response.ok);
      if (response.ok) { const session = await response.json() as { roles?: string[]; name?: string }; setRoles(session.roles ?? []); setUserName(session.name ?? ''); }
      else { setRoles([]); setUserName(''); }
    }).catch(() => { if (alive) { setSessionApiAvailable(false); setAuthenticated(false); setRoles([]); setUserName(''); } });
    return () => { alive = false; };
  }, [path]);

  useEffect(() => {
    for (const profile of shell.current?.querySelectorAll<HTMLElement>('[data-login]') ?? []) {
      profile.setAttribute('aria-label', isAuthenticated ? 'Se déconnecter' : 'Se connecter');
      const label = profile.querySelector('.profile-name, .profile-label') ?? profile.children.item(1);
      if (label) label.textContent = isAuthenticated ? 'Déconnexion' : 'Connexion';
    }
  }, [isAuthenticated, page]);

  const notify = useCallback((message: string) => {
    setStatus(message);
    window.setTimeout(() => setStatus((current) => current === message ? '' : current), 3200);
  }, []);

  const navigate = useCallback((href: string) => {
    if (!window.dispatchEvent(new CustomEvent('governance:navigate', { cancelable: true, detail: { href } }))) return true;
    const url = new URL(href, location.href);
    const requestedPath = canonicalPath(url);
    if (requestedPath !== location.pathname) rememberPilotageLocation();
    if (pilotageApp(requestedPath) && url.pathname === location.pathname && url.search === location.search && url.hash) {
      const id = decodeURIComponent(url.hash.slice(1));
      if (document.getElementById(id)) {
        window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        return true;
      }
    }
    const route = ROUTES.find((candidate) => requestedPath === candidate || requestedPath.endsWith(candidate))
      ?? (/^\/kpis\/[A-Za-z0-9_-]+$/.test(requestedPath) ? requestedPath : undefined);
    if (!route && url.pathname === location.pathname) {
      if (url.hash) document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView({ behavior: 'smooth' });
      return false;
    }
    if (!route && url.pathname.endsWith('/index.html')) return false;
    const target = route ?? '/';
    if (pageRef.current?.scripts?.length || (route && referenceForPath(route).scripts?.length)) {
      location.assign(`${target}${url.search}${url.hash}`);
      return true;
    }
    window.history.pushState({}, '', `${target}${url.search}${url.hash}`);
    setPath(target); setNavigationVersion(value => value + 1); window.scrollTo({ top: 0, behavior: 'instant' });
    return true;
  }, []);

  useEffect(() => {
    const onPopState = () => { if (!window.dispatchEvent(new CustomEvent('governance:navigate', { cancelable: true, detail: { href: location.href, popstate: true } }))) return; setPath(location.pathname); setNavigationVersion(value => value + 1); };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const onClick = useCallback(async (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const login = target.closest('[data-login]');
    if (login) {
      if (!isAuthenticated) { window.location.assign(`/connexion?returnUrl=${encodeURIComponent(`${location.pathname}${location.search}`)}`); return; }
      try { const response = await request('/auth/logout', { method: 'POST' }); if (!response.ok) throw new Error(await apiError(response)); window.location.assign('/'); }
      catch (error) { notify(error instanceof Error ? error.message : 'Déconnexion impossible.'); }
      return;
    }
    const logout = target.closest('[data-logout]');
    if (logout) {
      try { const response = await request('/auth/logout', { method: 'POST' }); if (!response.ok) throw new Error(await apiError(response)); window.location.assign('/'); }
      catch (error) { notify(error instanceof Error ? error.message : 'Déconnexion impossible.'); }
      return;
    }
    const toggle = target.closest('[data-theme-toggle]');
    if (toggle) {
      if (pageRef.current?.scripts?.length) return;
      const theme = document.documentElement.dataset.theme === 'light' ? 'dark' : 'light';
      document.documentElement.dataset.theme = theme;
      localStorage.setItem('governance-theme', theme);
      toggle.setAttribute('aria-pressed', String(theme === 'light'));
      toggle.setAttribute('aria-label', theme === 'light' ? 'Activer le thème sombre' : 'Activer le thème clair');
      return;
    }
    const anchor = target.closest('a[href]');
    if (anchor instanceof HTMLAnchorElement && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      if (event.defaultPrevented || event.button !== 0 || anchor.hasAttribute('download') ||
        anchor.origin !== location.origin || anchor.target && anchor.target !== '_self' ||
        ['/api/', '/auth/', '/Account/', '/connect/'].some(prefix => anchor.pathname.startsWith(prefix))) return;
      if (navigate(anchor.href)) { event.preventDefault(); return; }
    }
    const action = target.closest<HTMLElement>('button[data-refresh], [data-review], [data-certify], [data-reject], [data-correction], [data-save], [data-publish], [data-create-kpis], [data-attach-evidence], [data-placeholder-link]');
    if (!action) return;
    if (action.hasAttribute('data-placeholder-link')) { event.preventDefault(); notify('Cette intégration sera activée après configuration du connecteur.'); return; }
    if (action.hasAttribute('data-attach-evidence')) {
      const card = action.closest<HTMLElement>('[data-certification-card]');
      const observationId = card?.dataset.observationId;
      const inputs = card?.querySelectorAll<HTMLInputElement>('.evidence-attach input');
      if (!observationId || !inputs?.[0]?.value.trim() || !inputs[1]?.value.trim()) { notify('Renseignez le driveId et l’itemId du document SharePoint.'); return; }
      action.setAttribute('disabled', 'true');
      try {
        const response = await request(`/api/governance/observations/${observationId}/evidence`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ driveId: inputs[0].value.trim(), itemId: inputs[1].value.trim() }) });
        if (!response.ok) throw new Error(await apiError(response));
        const verifiedDocument = await response.json() as { fileName?: string; webUrl?: string };
        const label = verifiedDocument.fileName || 'Document SharePoint vérifié';
        const details = card?.querySelector<HTMLElement>('.provenance-content');
        if (details) details.textContent = `Preuve SharePoint vérifiée : ${label}`;
        if (verifiedDocument.webUrl && details) { const link = document.createElement('a'); link.href = verifiedDocument.webUrl; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.textContent = ' Ouvrir'; details.append(link); }
        notify('Preuve SharePoint vérifiée et liée à cette version.');
      } catch (error) { notify(error instanceof Error ? error.message : 'Vérification SharePoint impossible.'); }
      finally { action.removeAttribute('disabled'); }
      return;
    }
    const code = action.closest('[data-certification-card]')?.querySelector('.kpi-code')?.textContent?.trim();
    const observationId = action.closest('[data-certification-card]')?.getAttribute('data-observation-id');
    const transition = action.hasAttribute('data-review') ? 'review' : action.hasAttribute('data-certify') ? 'certify' : action.hasAttribute('data-reject') ? 'reject' : action.hasAttribute('data-correction') ? 'request-correction' : '';
    if (transition && observationId) {
      const comment = action.closest('[data-certification-card]')?.querySelector('textarea') as HTMLTextAreaElement | null;
      if ((transition === 'reject' || transition === 'request-correction') && !comment?.value.trim()) { notify('Saisissez un commentaire pour motiver le rejet ou la demande de correction.'); comment?.focus(); return; }
      try {
        const response = await request(`/api/governance/observations/${observationId}/${transition}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: comment?.value ?? '' }) });
        if (!response.ok) throw new Error(await apiError(response));
        const card = action.closest<HTMLElement>('[data-certification-card]');
        if (transition === 'review' || transition === 'certify') {
          if (card) updateCertificationActions(card, transition === 'review' ? 'in-review' : 'certified');
          notify(transition === 'review' ? 'Observation prise en revue.' : 'Valeur certifiée. Elle peut maintenant être publiée.');
        } else { card?.remove(); notify(transition === 'reject' ? 'Observation rejetée et décision journalisée.' : 'Correction demandée et décision journalisée.'); }
      } catch (error) { notify(error instanceof Error ? error.message : 'La décision n’a pas été enregistrée.'); }
      return;
    }
    if (action.hasAttribute('data-publish') && observationId) {
      try {
        const response = await request(`/api/governance/observations/${observationId}/publish`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment: '' }) });
        if (!response.ok) throw new Error(await apiError(response));
        action.closest('[data-certification-card]')?.remove();
        notify('Valeur certifiée publiée dans le référentiel.');
      } catch (error) { notify(error instanceof Error ? error.message : 'Publication impossible.'); }
      return;
    }
    if (action.hasAttribute('data-review') || action.hasAttribute('data-certify') || action.hasAttribute('data-reject') || action.hasAttribute('data-correction')) {
      notify(`La valeur ${code ? `« ${code} » ` : ''}doit être enregistrée dans le référentiel avant certification.`); return;
    }
    if (action.hasAttribute('data-refresh')) {
      try {
        const response = await request('/api/governance/dashboard');
        if (!response.ok) throw new Error(await apiError(response));
        notify('Les données vérifiées ont été actualisées.');
      } catch (error) { notify(error instanceof Error ? error.message : 'Actualisation impossible.'); }
      return;
    }
    if (action.hasAttribute('data-save') || action.hasAttribute('data-publish')) {
      notify('Cette fiche doit être reliée à une définition du référentiel avant publication.'); return;
    }
    if (action.hasAttribute('data-create-kpis')) {
      const batchId = shell.current?.getAttribute('data-import-batch');
      if (!batchId || action instanceof HTMLButtonElement && action.disabled) { notify('Sélectionnez un fichier valide et corrigez les erreurs avant confirmation.'); return; }
      action.setAttribute('disabled', 'true');
      try {
        const response = await request(`/api/governance/imports/${encodeURIComponent(batchId)}/confirm`, { method: 'POST' });
        if (!response.ok) throw new Error(await apiError(response));
        const result = await response.json() as { created?: number; status?: string };
        const results = shell.current?.querySelector<HTMLElement>('[data-import-results]');
        const complete = shell.current?.querySelector<HTMLElement>('[data-import-complete]');
        if (results) results.hidden = true;
        const completionMessage = result.created === 1
          ? '1 KPI a été créé et rattaché à son contrat. Il est disponible pour la saisie des valeurs.'
          : typeof result.created === 'number'
            ? `${result.created} KPI ont été créés et rattachés à leurs contrats. Ils sont disponibles pour la saisie des valeurs.`
            : 'Les KPI valides ont été créés et rattachés à leurs contrats. Ils sont disponibles pour la saisie des valeurs.';
        if (complete) { complete.hidden = false; setText(complete, 'h2', 'Import confirmé'); setText(complete, 'p', completionMessage); }
        notify('Import confirmé : les KPI ont été créés et rattachés à leurs contrats.');
      } catch (error) { action.removeAttribute('disabled'); notify(error instanceof Error ? error.message : 'La confirmation de l’import a échoué.'); }
      return;
    }
    if (action.hasAttribute('data-reload-import')) { shell.current?.querySelector<HTMLInputElement>('[data-kpi-file]')?.click(); return; }
    if (action.hasAttribute('data-clear-file')) {
      const input = shell.current?.querySelector<HTMLInputElement>('[data-kpi-file]'); if (input) input.value = '';
      const selected = shell.current?.querySelector<HTMLElement>('[data-selected-file]'); if (selected) selected.hidden = true;
      const results = shell.current?.querySelector<HTMLElement>('[data-import-results]'); if (results) results.hidden = true;
      shell.current?.removeAttribute('data-import-batch'); return;
    }
  }, [isAuthenticated, navigate, notify]);

  const onChange = useCallback(async (event: React.ChangeEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;
    if (target instanceof HTMLInputElement && target.hasAttribute('data-kpi-file') && target.type === 'file' && target.files?.[0]) {
      const form = new FormData(); form.set('file', target.files[0]);
      try {
        const response = await request('/api/governance/imports/preview', { method: 'POST', body: form });
        if (!response.ok) throw new Error(await apiError(response));
        const preview = await response.json() as ImportPreviewResult;
        if (!shell.current) return;
        renderImportPreview(shell.current, preview);
        const selected = shell.current.querySelector<HTMLElement>('[data-selected-file]');
        if (selected) selected.hidden = false;
        setText(shell.current, '[data-selected-name]', target.files[0].name);
        setText(shell.current, '[data-selected-details]', `${(target.files[0].size / 1024).toFixed(1)} Ko · ${preview.validRows} ligne(s) valide(s), ${preview.errorRows} ligne(s) à corriger`);
        const complete = shell.current.querySelector<HTMLElement>('[data-import-complete]'); if (complete) complete.hidden = true;
        notify(`Contrôle terminé : ${preview.validRows} ligne(s) prête(s), ${preview.errorRows} erreur(s). Aucune donnée n’a encore été créée.`);
      } catch (error) { notify(error instanceof Error ? error.message : 'Impossible de valider le fichier.'); }
      return;
    }
    const search = target.matches('[data-search], [data-cockpit-search], [data-catalogue-search]') ? target : null;
    if (search) {
      const query = normalize(search.value.trim());
      const cards = Array.from(shell.current?.querySelectorAll<HTMLElement>('[data-searchable], [data-certification-card]') ?? []);
      for (const card of cards) card.hidden = query !== '' && !normalize(`${card.dataset.searchable ?? ''} ${card.innerText}`).includes(query);
      const results = cards.filter((card) => !card.hidden).length;
      const empty = shell.current?.querySelector<HTMLElement>('[data-kpi-empty], [data-catalogue-empty], [data-empty]');
      if (empty) empty.hidden = results !== 0;
    }
    if (target.matches('[data-kpi-filter], [data-status-filter]')) {
      const selected = target.value;
      for (const card of shell.current?.querySelectorAll<HTMLElement>('.command-kpi-card, .catalogue-table tbody tr') ?? []) {
        const accepted = selected === 'all' || card.dataset.status === selected || card.dataset.status === ({ critical: 'critical', watch: 'watch', okay: 'okay' } as Record<string, string>)[selected];
        card.hidden = !accepted;
      }
    }
  }, [notify]);

  const toggleTheme = useCallback(() => {
    const theme = document.documentElement.dataset.theme === 'light' ? 'dark' : 'light';
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('governance-theme', theme);
  }, []);

  if (!page) return <main className="boot-screen"><img src="/ARTCI_logo.svg" alt="ARTCI" />{status || 'Chargement de Gouvernance…'}</main>;
  const { visiblePath: renderPath, pending: routePending } = getRoutePresentation(readyPath, path);
  const routeBusy = routePending || transitionVisible;
  return <div ref={shell} className="application-root" data-route-pending={routeBusy || undefined} aria-busy={routeBusy} onClick={onClick} onChange={onChange}>
    {routeBusy && <div className="route-loader-overlay" role="status" aria-live="polite"><div className="route-loader-card"><span className="route-loader-spinner" aria-hidden="true"/><strong>Chargement de la page</strong><span>La vue se prépare…</span></div></div>}
    {routePending && <div className="route-progress" aria-hidden="true"><span /></div>}
    <CollaborationProvider request={request} authenticated={isAuthenticated} admin={isAdmin}><PilotageShell path={renderPath} isAuthenticated={isAuthenticated} isAdmin={isAdmin} userName={userName} onToggleTheme={toggleTheme}>
    {['/mon-espace', '/saisie-kpi', '/certification-kpi', '/kpis/nouveau'].includes(renderPath) ? <Suspense fallback={<main className="boot-screen">Chargement de votre espace de travail…</main>}><OperationalWorkspace key={`workspace:${navigationVersion}:${isAuthenticated}`} request={request} authenticated={isAuthenticated}/></Suspense> : renderPath === '/mes-tableaux' ? <DashboardWorkspace key={`dashboards:${navigationVersion}`} request={request} authenticated={isAuthenticated} admin={isAdmin}/> : renderPath === '/collaboration' ? <CollaborationWorkspace key={`collaboration:${navigationVersion}`} /> : renderPath === '/prerequis' ? <PrerequisitesWorkspace key={`prerequisites:${navigationVersion}`} /> : renderPath === '/connexion' ? <AuthenticationChoice /> : renderPath === '/administration' ? <AdministrationCenter isAdmin={isAdmin} request={request} notify={notify} /> : renderPath === '/couverture-qos' ? <Suspense fallback={<main className="boot-screen"><img src="/ARTCI_logo.svg" alt="ARTCI" />Chargement de Couverture QoS…</main>}><CouvertureQos isAdmin={isAdmin} request={request} /></Suspense> : renderPath === '/observatoire-qos' ? <Suspense fallback={<main className="boot-screen"><img src="/ARTCI_logo.svg" alt="ARTCI" />Chargement de l’Observatoire QoS…</main>}><ObservatoireQos isAdmin={isAdmin} request={request} /></Suspense> : renderPath === '/saisie-kpi' ? <KpiValueEntry request={request} isAdmin={isAdmin} onToggleTheme={toggleTheme} /> : renderPath === '/import-kpi' ? <KpiImport request={request} isAdmin={isAdmin} onToggleTheme={toggleTheme} /> : renderPath === '/signaux' ? <SignalQueue request={request} onToggleTheme={toggleTheme} /> : renderPath === '/certification-kpi' ? <KpiCertification request={request} isValidator={isAdmin || roles.includes('Gouvernance.Validator')} isContributor={isAdmin || roles.includes('Gouvernance.Contributor') || roles.includes('Gouvernance.Validator')} onToggleTheme={toggleTheme} notify={notify} /> : renderPath === '/pilotage-directions' ? <DirectionPortfolio isAdmin={isAdmin} request={request} /> : renderPath === '/direction/index.html' || renderPath === '/tableau-dg' ? <ExecutiveDashboard key={`dg:${navigationVersion}:${isAuthenticated}:${userName}`} request={request} authenticated={isAuthenticated} userName={userName} admin={isAdmin}/> : renderPath === '/cockpit' ? <ExecutiveCockpit isAuthenticated={isAuthenticated} sessionApiAvailable={sessionApiAvailable} roles={roles} onLogin={() => window.location.assign(`/connexion?returnUrl=${encodeURIComponent(`${location.pathname}${location.search}`)}`)} /> : renderPath === '/catalogue-kpi' ? <KpiCatalog isAuthenticated={isAuthenticated} onToggleTheme={toggleTheme} /> : renderPath.startsWith('/kpis/') ? <KpiDefinition path={renderPath} isAuthenticated={isAuthenticated} isAdmin={false} onToggleTheme={toggleTheme} notify={notify} request={request} /> : renderPath === '/arbres-kpi' ? <PerformanceTrees isAdmin={isAdmin} request={request} /> : <>
      {page.tree}
    </>}
    </PilotageShell></CollaborationProvider>
    {status && <p className="application-toast" role="status">{status}</p>}
  </div>;
}

function AuthenticationChoice() {
  const [entraAvailable, setEntraAvailable] = useState(false);
  const [localAvailable, setLocalAvailable] = useState(true);
  const [returnUrl] = useState(() => {
    const value = new URLSearchParams(location.search).get('returnUrl') ?? '/';
    return value.startsWith('/') && !value.startsWith('//') && !value.includes('\\') ? value : '/';
  });
  useEffect(() => {
    let alive = true;
    fetch('/auth/providers', { credentials: 'same-origin' }).then(response => response.ok ? response.json() : null)
      .then((providers: { local?: boolean; entra?: boolean } | null) => {
        if (alive) { setEntraAvailable(providers?.entra === true); setLocalAvailable(providers?.local !== false); }
      })
      .catch(() => { if (alive) setEntraAvailable(false); });
    return () => { alive = false; };
  }, []);
  return <main className="authentication-choice">
    <a href="/">← Retour au portail</a>
    <img src="/ARTCI_logo.svg" alt="ARTCI" />
    <p className="eyebrow">ESPACE SÉCURISÉ</p>
    <h1>Choisissez votre mode de connexion</h1>
    <p>Les indicateurs restent consultables sans compte. Une connexion est requise pour contribuer, certifier ou administrer le portail.</p>
    {localAvailable && <a className="button button-primary" href={`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`}>Compte Gouvernance ABP</a>}
    {entraAvailable && <a className="button" href={`/auth/login/entra?returnUrl=${encodeURIComponent(returnUrl)}`}>Compte Microsoft Entra ID</a>}
    {!localAvailable && !entraAvailable && <p role="status">Le fournisseur de connexion institutionnel n’est pas disponible. Contactez l’administrateur.</p>}
  </main>;
}

interface Grant { userId: string; directionCode: string; creationTime: string; validFromUtc: string; validToUtc: string | null; }
interface RoleAssignment { userId: string; roleName: string; creationTime: string; validFromUtc: string; validToUtc: string | null; }
interface AdminDirection { code: string; name: string; }
interface AdminContract { id: string; code: string; name: string; directionCode: string; startsOn: string; endsOn: string; kpiCount: number; }
const LOCALLY_ASSIGNABLE_ROLES = [
  { value: 'Gouvernance.Reader', label: 'Lecteur' },
  { value: 'Gouvernance.Contributor', label: 'Contributeur' },
  { value: 'Gouvernance.Validator', label: 'Validateur' },
];

export function AdminConsole({ isAdmin, notify }: { isAdmin: boolean; notify: (message: string) => void }) {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const [directions, setDirections] = useState<AdminDirection[]>([]);
  const [contracts, setContracts] = useState<AdminContract[]>([]);
  const [userId, setUserId] = useState('');
  const [directionCode, setDirectionCode] = useState('');
  const [roleName, setRoleName] = useState(LOCALLY_ASSIGNABLE_ROLES[0].value);
  const [newDirection, setNewDirection] = useState({ code: '', name: '' });
  const [newContract, setNewContract] = useState({ code: '', name: '', directionCode: '', startsOn: '', endsOn: '' });
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const [grantResponse, directionResponse, contractResponse, roleResponse] = await Promise.all([
        request('/api/governance/admin/directions'), request('/api/governance/admin/directions/catalog'), request('/api/governance/admin/contracts'), request('/api/governance/admin/roles'),
      ]);
      if (!grantResponse.ok || !directionResponse.ok || !contractResponse.ok || !roleResponse.ok) throw new Error(await apiError(!grantResponse.ok ? grantResponse : !directionResponse.ok ? directionResponse : !contractResponse.ok ? contractResponse : roleResponse));
      setGrants(await grantResponse.json() as Grant[]);
      setRoleAssignments(await roleResponse.json() as RoleAssignment[]);
      const nextDirections = await directionResponse.json() as AdminDirection[];
      setDirections(nextDirections);
      setContracts(await contractResponse.json() as AdminContract[]);
      setDirectionCode((current) => current || nextDirections[0]?.code || '');
      setNewContract((current) => ({ ...current, directionCode: current.directionCode || nextDirections[0]?.code || '' }));
      setError('');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Chargement des périmètres impossible.'); }
  }, []);
  useEffect(() => { if (isAdmin) void load(); }, [isAdmin, load]);

  const createDirection = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const response = await request('/api/governance/admin/directions', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newDirection) });
      if (!response.ok) throw new Error(await apiError(response));
      setNewDirection({ code: '', name: '' }); await load(); notify('Direction créée.');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Création de la direction impossible.'); }
  };

  const createContract = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const response = await request('/api/governance/admin/contracts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newContract) });
      if (!response.ok) throw new Error(await apiError(response));
      setNewContract((current) => ({ code: '', name: '', directionCode: current.directionCode, startsOn: '', endsOn: '' })); await load(); notify('Contrat créé.');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Création du contrat impossible.'); }
  };

  const assign = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId)) { setError('Saisissez l’Object ID Entra au format UUID.'); return; }
    try {
      const response = await request(`/api/governance/admin/directions/${encodeURIComponent(directionCode)}/users/${userId}`, { method: 'POST' });
      if (!response.ok) throw new Error(await apiError(response));
      setUserId(''); await load(); notify('Périmètre de direction attribué.');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Attribution impossible.'); }
  };

  const assignRole = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId)) { setError('Saisissez l’Object ID Entra au format UUID.'); return; }
    try {
      const response = await request('/api/governance/admin/roles', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId, roleName }) });
      if (!response.ok) throw new Error(await apiError(response));
      setUserId(''); await load(); notify('Rôle applicatif attribué.');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Attribution du rôle impossible.'); }
  };

  const revokeRole = async (assignment: RoleAssignment) => {
    try {
      const response = await request(`/api/governance/admin/roles/${encodeURIComponent(assignment.roleName)}/users/${assignment.userId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await apiError(response));
      await load(); notify('Rôle applicatif retiré.');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Retrait du rôle impossible.'); }
  };

  const revoke = async (grant: Grant) => {
    try {
      const response = await request(`/api/governance/admin/directions/${encodeURIComponent(grant.directionCode)}/users/${grant.userId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await apiError(response));
      await load(); notify('Périmètre direction retiré.');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Retrait impossible.'); }
  };

  if (!isAdmin) return <main className="admin-console"><a href="/">← Retour au portail</a><h1>Administration protégée</h1><p>Un compte authentifié disposant du rôle Administrateur Gouvernance est requis.</p><a href="/auth/login">Se connecter avec un compte Gouvernance</a> · <a href="/auth/login/entra">Se connecter avec Microsoft</a></main>;
  return <main className="admin-console">
    <a href="/">← Retour au portail</a>
    <header><img src="/ARTCI_logo.svg" alt="ARTCI" /><div><p>ADMINISTRATION</p><h1>Accès et rôles</h1></div></header>
    <p className="admin-explainer">Cette console attribue les rôles Lecteur, Contributeur et Validateur ainsi que les périmètres directionnels. Les rôles globaux Administrateur, DG et Auditeur restent administrés dans Entra ID.</p>
    <form className="admin-grant-form" onSubmit={assign}>
      <label>Object ID de l’utilisateur Entra<input value={userId} onChange={(event) => setUserId(event.target.value)} required placeholder="00000000-0000-4000-8000-000000000000" /></label>
      <label>Direction<select value={directionCode} onChange={(event) => setDirectionCode(event.target.value)} required><option value="">Choisir une direction</option>{directions.map((item) => <option key={item.code} value={item.code}>{item.name} · {item.code}</option>)}</select></label>
      <button type="submit">Attribuer le périmètre</button>
    </form>
    <form className="admin-grant-form admin-role-form" onSubmit={assignRole}>
      <label>Object ID de l’utilisateur Entra<input value={userId} onChange={(event) => setUserId(event.target.value)} required placeholder="00000000-0000-4000-8000-000000000000" /></label>
      <label>Rôle applicatif<select value={roleName} onChange={(event) => setRoleName(event.target.value)}>{LOCALLY_ASSIGNABLE_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}</select></label>
      <button type="submit">Attribuer le rôle</button>
    </form>
    {error && <p className="admin-error" role="alert">{error}</p>}
    <h2>Référentiel des directions et contrats</h2>
    <section className="admin-reference-grid" aria-label="Créer une direction ou un contrat">
      <form className="admin-reference-form" onSubmit={createDirection}>
        <h3>Nouvelle direction</h3>
        <label>Code<input value={newDirection.code} onChange={(event) => setNewDirection({ ...newDirection, code: event.target.value })} required maxLength={40} placeholder="DIRECTION-01" /></label>
        <label>Nom<input value={newDirection.name} onChange={(event) => setNewDirection({ ...newDirection, name: event.target.value })} required maxLength={240} /></label>
        <button type="submit">Créer la direction</button>
      </form>
      <form className="admin-reference-form" onSubmit={createContract}>
        <h3>Nouveau contrat d’objectifs</h3>
        <label>Code<input value={newContract.code} onChange={(event) => setNewContract({ ...newContract, code: event.target.value })} required maxLength={80} placeholder="S2-2026-DIRECTION-01" /></label>
        <label>Nom<input value={newContract.name} onChange={(event) => setNewContract({ ...newContract, name: event.target.value })} required maxLength={240} /></label>
        <label>Direction<select value={newContract.directionCode} onChange={(event) => setNewContract({ ...newContract, directionCode: event.target.value })} required><option value="">Choisir une direction</option>{directions.map((item) => <option key={item.code} value={item.code}>{item.name} · {item.code}</option>)}</select></label>
        <label>Début<input type="date" value={newContract.startsOn} onChange={(event) => setNewContract({ ...newContract, startsOn: event.target.value })} required /></label>
        <label>Fin<input type="date" value={newContract.endsOn} onChange={(event) => setNewContract({ ...newContract, endsOn: event.target.value })} required /></label>
        <button type="submit" disabled={directions.length === 0}>Créer le contrat</button>
      </form>
    </section>
    <h2>Contrats disponibles pour les imports ({contracts.length})</h2>
    <div className="admin-grants">{contracts.map((contract) => <article key={contract.id}><div><strong>{contract.name}</strong><code>{contract.code} · {contract.directionCode}</code><small>{contract.startsOn} → {contract.endsOn} · {contract.kpiCount} KPI</small></div></article>)}</div>
    <h2>Rôles applicatifs ({roleAssignments.filter((item) => item.validToUtc === null).length})</h2>
    <div className="admin-grants">{roleAssignments.map((assignment) => <article key={`${assignment.userId}-${assignment.roleName}-${assignment.validFromUtc}`}><div><strong>{LOCALLY_ASSIGNABLE_ROLES.find((role) => role.value === assignment.roleName)?.label ?? assignment.roleName}</strong><code>{assignment.userId}</code><small>{new Date(assignment.validFromUtc).toLocaleString('fr-FR')} → {assignment.validToUtc ? new Date(assignment.validToUtc).toLocaleString('fr-FR') : 'en cours'}</small></div>{assignment.validToUtc === null && <button type="button" onClick={() => void revokeRole(assignment)}>Retirer</button>}</article>)}</div>
    <h2>Périmètres attribués ({grants.length})</h2>
    <div className="admin-grants">{grants.map((grant) => <article key={`${grant.userId}-${grant.directionCode}-${grant.validFromUtc}`}><div><strong>{directions.find((item) => item.code === grant.directionCode)?.name ?? grant.directionCode}</strong><code>{grant.userId}</code><small>{new Date(grant.validFromUtc).toLocaleString('fr-FR')} → {grant.validToUtc ? new Date(grant.validToUtc).toLocaleString('fr-FR') : 'en cours'}</small></div>{grant.validToUtc === null && <button type="button" onClick={() => void revoke(grant)}>Retirer</button>}</article>)}</div>
    <p className="admin-note">Une attribution de rôle globale dans Entra ID et un périmètre de direction sont deux contrôles distincts. Les décisions sont vérifiées côté serveur.</p>
  </main>;
}

function normalize(value: string): string { return value.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }
async function apiError(response: Response): Promise<string> {
  if (response.status === 401) return 'Connectez-vous avec votre compte Gouvernance ou Microsoft pour continuer.';
  if (response.status === 403) return 'Votre rôle ou votre périmètre ne permet pas cette action.';
  const body = await response.json().catch(() => null) as { error?: { message?: string }; title?: string } | null;
  return body?.error?.message ?? body?.title ?? `La requête a échoué (${response.status}).`;
}

createRoot(document.getElementById('root')!).render(<StrictMode><App /></StrictMode>);
